/**
 * THE PRIVACY POLICY, WRITTEN ONCE (PV5a) — prest0.ai/privacy-policy, in today's design (src/pages/Privacy.tsx) and in
 * the new design's reading page (src/design/pavilion/pages/privacy-policy/). See ./legalDocument.ts for how a document is
 * shared.
 *
 * Moved here from Privacy.tsx as written, part names aside; the page's rendered HTML was compared byte for byte before
 * and after the move. The date it took effect is src/config/legalVersions.ts's (EffectiveDate), and a no-JavaScript
 * copy is published as public/privacy-policy.html (kept by hand; scripts/check-legal-effective-date.mjs explains why).
 *
 * /signup links to /privacy-policy#sms: the SMS section keeps its own id for the contents and carries a second address,
 * `sms`, drawn by the `Anchor` part (see the PRE-72 note in that section).
 *
 * 2026-10-17: the role (controller of Prest0's own data, the firm's processor for its case and client content), what is
 * collected before an account exists, who receives texts and call audio, where data is stored and who reaches it from
 * abroad, the children's and contact sections and the response times, each read from the code or the platform facts of
 * 2026-10-16 (legalVersions.ts says what changed).
 */
import React from 'react';
import type { LegalDocument, LegalParts } from './legalDocument';

/** What the page says above the document, and what its contents are called. */
export const PRIVACY_POLICY_HEAD = {
  heading: 'privacy policy',
  contentsTitle: 'on this page',
  contentsName: 'privacy policy table of contents',
} as const;

/** The key takeaways the page shows before the document. */
export const PRIVACY_POLICY_SUMMARY = {
  badge: 'key takeaways',
  heading: 'we protect your data so you can build ai agents with confidence',
  items: [
    'no selling or renting of personal information—ever.',
    'for the case and client content your firm places on prest0, we act as your firm’s processor and follow its instructions.',
    'tls in transit and aes-256 at rest on google cloud protect every workspace, and sign-in is through google or an email and password. google cloud’s soc 2 type ii and iso/iec 27001 reports cover its own infrastructure, and where our own audits stand is on the security page.',
    'every request through our model gateway carries zero-data-retention and no-collection flags. we are committed to your prompts, transcripts and attachments never training shared models, and no training and zero retention at every provider is targeted for october 31, 2026.',
    'you can download a copy of your data, and ask us to delete it, from your settings page. a deletion request tells you what will go and what we have to keep. a deletion request can be cancelled for 30 days; we carry it out after that window closes, and in any case within 45 days of the request, except where a legal hold or legal, tax or regulatory duties require us to keep records.',
  ],
} as const;

export type PrivacyPolicyParts = Pick<
  LegalParts,
  'Text' | 'List' | 'ListItem' | 'SubTitle' | 'PageLink' | 'OutLink' | 'Facts' | 'Fact' | 'FactLabel' | 'FactValue' | 'FactNote' | 'Note' | 'NoteText' | 'Anchor'
>;

/** The privacy policy, in the parts the caller draws it with. */
export function privacyPolicyDocument({
  Text, List, ListItem, SubTitle, PageLink, OutLink, Facts, Fact, FactLabel, FactValue, FactNote, Note, NoteText, Anchor,
}: PrivacyPolicyParts): LegalDocument {
  return {
    sections: [
      {
        id: 'overview',
        heading: 'overview',
        body: (
          <>
            <Text>
              prest0 ("prest0", "we", "our", or "us") builds ai agent workflows that help teams ship automations without code. This privacy policy explains how we collect, use, store, and share personal information for visitors, builders, investors, and partners who interact with the live experience at prest0.ai and any related services that link to this notice (collectively, the "services").
            </Text>
            <Text>
              By using the services, you agree to the practices described here as well as in our <PageLink to="/terms-of-service">terms of service</PageLink> and <PageLink to="/cookie-policy">cookie policy</PageLink>. If you disagree with this policy, please discontinue use of the services.
            </Text>
            <Text>
              The privacy commitments below match what we publish on the security page: TLS in transit, AES-256 encryption at rest on google cloud, sign-in through Google or an email and password with permissions resolved per person, documented retention schedules with deletion on request, and our commitment that your content never trains shared models, with no training and zero retention at every model provider targeted for October 31, 2026.
            </Text>
            <Facts>
              <Fact>
                <FactLabel>our role</FactLabel>
                <FactValue>processor for your firm’s content</FactValue>
                <FactNote>controller of our own account, billing, website, sales and staff data</FactNote>
              </Fact>
              <Fact>
                <FactLabel>security baseline</FactLabel>
                <FactValue>tls + aes-256</FactValue>
                <FactNote>matches the controls in our security page</FactNote>
              </Fact>
              <Fact>
                <FactLabel>retention controls</FactLabel>
                <FactValue>deletion on request</FactValue>
                <FactNote>cancellable for 30 days, carried out within 45</FactNote>
              </Fact>
            </Facts>
            <Text>
              This policy applies whether you access prest0 directly, participate in private beta programs, engage with us through live demonstrations, or communicate with our team via chat, email, or social channels.
            </Text>
          </>
        ),
      },
      {
        id: 'roles',
        heading: 'who is responsible for your information',
        body: (
          <>
            <Text>
              prest0 is the controller of its own account, billing, website, sales and staff data: the information about the people who sign up, pay, visit prest0.ai, talk with our sales team or work with us.
            </Text>
            <Text>
              For the case and client content a customer firm places on the platform (its matters, documents and messages, and the files and connected-app data it works with), prest0 acts as the firm's processor, a service provider under the CCPA, and processes that content only on the firm's instructions, under the firm's order form (our Subscription Agreement and Purchase Order) and our <PageLink to="/terms-of-service">terms of service</PageLink>.
            </Text>
            <Text>
              If a firm has put information about you on the platform, for example because you are its client or a party to one of its matters, please contact that firm: it decides how your information is used. If you contact us instead, we will refer you to the firm and help it answer you.
            </Text>
          </>
        ),
      },
      {
        id: 'information-we-collect',
        heading: 'information we collect',
        body: (
          <>
            <SubTitle>information you provide to us</SubTitle>
            <List>
              <ListItem>account details such as name, email address, company, and authentication credentials when you create or manage a prest0 account.</ListItem>
              <ListItem>profile preferences, notification settings, and any configuration data you save inside your workspace.</ListItem>
              <ListItem>support requests, survey responses, chat conversations, and other communications with our team.</ListItem>
              <ListItem>billing contact data and limited payment-related metadata when you engage in paid features (payment details are stored securely by our payment processor).</ListItem>
              <ListItem>if you talk to Prest0n by voice, your microphone audio, and camera images if you turn the camera on, which Google's Gemini Live receives to run the voice conversation.</ListItem>
            </List>
            <SubTitle>information collected automatically</SubTitle>
            <List>
              <ListItem>log data such as browser type, device information, operating system, and ip address used to access the services.</ListItem>
              <ListItem>server logs from the requests your browser makes — IP address, browser metadata and timestamps. We run no analytics product, so we do not build a record of pages you viewed, features you clicked, how long you stayed, or where you came from.</ListItem>
              <ListItem>when you are signed in, the chat reports its own health to our servers so we can find and fix problems you run into: counts and timings such as how long a thread took to load, how often the page froze, for how long, which part of our own code or of the browser's own storage was busy and how large the page was, and whether a message failed to start, tagged with your account and workspace. it never includes what you write, file names, or the pages you visit, and it is kept only in our server logs. if you are not signed in, none of it leaves your browser.</ListItem>
              <ListItem>cookie identifiers, local storage and similar technologies used to keep you signed in, remember preferences, maintain session security and protect payments — never to track you for analytics or advertising.</ListItem>
              <ListItem>diagnostic data, crash reports, and trace logs generated automatically by our infrastructure providers.</ListItem>
            </List>
            <SubTitle>before you have an account</SubTitle>
            <List>
              <ListItem>if you chat with Prest0n on prest0.ai before creating an account, we keep that conversation for 30 days after your last message — the clock restarts each time you come back, so it is still there when you return and can be carried into your account if you sign up. stop using it and it is deleted automatically; you can also ask us to remove it straight away from our trust page, without an account. It is filed under a one-way hash of your IP address and also carries a hash of a random code your browser keeps for the chat; we do not store the address itself. Nothing about it is shared with an advertiser, because we have none.</ListItem>
              <ListItem>if you run research on prest0.ai before creating an account, your request (the question and any document text you add) and its report are kept for 24 hours. The downloadable report files it produces are not yet removed on that timer.</ListItem>
              <ListItem>if you use the chat widget a firm places on its own website, we keep your conversation with it, the name and email address you give, and, if you buy a research report through it, the report and the payment record. These are not yet deleted on a timer; we delete them on request.</ListItem>
              <ListItem>if you join a wait list, we keep your email address, first name, the LinkedIn and X links you choose to add and the message you send, until you ask us to remove them.</ListItem>
              <ListItem>if you text or call Prest0n's number, (628) 444-4018, we keep your number and your messages; the sms & text messaging section below says who receives them and how long consent records are kept.</ListItem>
            </List>
            <SubTitle>information from third parties</SubTitle>
            <List>
              <ListItem>authentication providers (such as google) that share tokens or profile basics when you opt in to single sign-on.</ListItem>
              <ListItem>payment processors (stripe) that confirm billing status, card brand, and the last four digits of payment methods.</ListItem>
              <ListItem>service partners that help deliver functionality (for example, cloud hosting or ai model providers) where permitted by their policies and yours.</ListItem>
            </List>
            <SubTitle>information generated by ai agents</SubTitle>
            <List>
              <ListItem>the prompts you send, the replies and files Prest0n produces, and the conversations they belong to, which are saved to your workspace.</ListItem>
              <ListItem>system telemetry that helps us monitor performance, latency, and error recovery for automated workflows.</ListItem>
              <ListItem>anonymized or aggregated insights derived from agent runs that cannot be traced back to an individual user.</ListItem>
            </List>
            <SubTitle>information collected via sms/text messaging</SubTitle>
            <List>
              <ListItem>phone number you provide when opting in to receive text messages from prest0.</ListItem>
              <ListItem>message content, delivery status, and opt-in/opt-out preferences associated with your mobile number.</ListItem>
              <ListItem>carrier information used for message routing and delivery confirmation.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'how-we-use-information',
        heading: 'how we use information',
        body: (
          <>
            <Text>
              We process personal information so we can operate a secure, reliable, and continuously improving ai agent experience. For the information we control (see who is responsible for your information above), we use data to:
            </Text>
            <List>
              <ListItem>deliver core product functionality, including authentication, workspace management, collaboration, and agent execution.</ListItem>
              <ListItem>support you with onboarding, documentation, success guidance, and customer care.</ListItem>
              <ListItem>maintain platform health, detect anomalies, prevent fraud, and troubleshoot issues.</ListItem>
              <ListItem>research and build new capabilities, including personalization and automation improvements.</ListItem>
              <ListItem>communicate service updates, security alerts, or administrative announcements.</ListItem>
              <ListItem>send transactional and informational text messages to your mobile phone when you opt in, such as case updates, appointment reminders, and account notifications.</ListItem>
              <ListItem>comply with legal obligations, enforce agreements, and protect the rights of prest0 and our users.</ListItem>
            </List>
            <Text>
              The case and client content a firm places on the platform is used only to provide the services to that firm, on its instructions.
            </Text>
            <Note>
              <NoteText>
                We do not sell or share personal information. We only use data to support the services you request, we run no advertising or analytics trackers, and we honor Global Privacy Control.
              </NoteText>
            </Note>
          </>
        ),
      },
      {
        id: 'legal-bases',
        heading: 'legal bases for processing',
        body: (
          <>
            <Text>
              prest0 serves customers in the United States today. Where a data protection law such as the gdpr or a comparable regulation applies to you, we rely on these legal bases:
            </Text>
            <List>
              <ListItem>performance of a contract when supplying requested services, maintaining your account, or providing support.</ListItem>
              <ListItem>legitimate interests to secure our infrastructure, improve features, and understand product adoption in a privacy-aware manner.</ListItem>
              <ListItem>compliance with legal obligations, such as tax, accounting, or regulatory requirements.</ListItem>
              <ListItem>consent for marketing communications, beta programs, or processing that is not strictly necessary. We run no optional analytics, so none is collected on this basis.</ListItem>
              <ListItem>protection of vital interests when responding to urgent safety matters or preventing harmful activity.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'information-sharing',
        heading: 'how we share information',
        body: (
          <>
            <Text>
              We share personal information only with the companies that help us operate, or when you direct us to do so:
            </Text>
            <List>
              <ListItem>infrastructure and product vendors (google cloud / firebase, and google when you sign in with it).</ListItem>
              <ListItem>payment partners (stripe) that securely manage billing and financial transactions.</ListItem>
              <ListItem>AI model providers, reached mostly through our model gateway. Every request through the gateway carries zero-data-retention and no-collection flags; no training and zero retention at every provider, fallbacks included, is targeted for October 31, 2026. Our current list of subprocessors, with what each receives and where it runs, is on our trust page at <OutLink href="https://prest0.ai/trust">prest0.ai/trust</OutLink>.</ListItem>
              <ListItem>legal authorities when required by law, court order, or to defend our rights.</ListItem>
              <ListItem>future corporate transactions, such as a merger or acquisition, where your information remains protected by this policy or a successor policy with equal safeguards.</ListItem>
            </List>
            <Note>
              <NoteText>
                We do not share personal information with anyone for their own marketing. Each company receives what it handles under its own terms or its agreement with us, and our trust page links each one's privacy terms.
              </NoteText>
            </Note>
          </>
        ),
      },
      {
        id: 'data-retention',
        heading: 'data retention',
        body: (
          <>
            <Text>
              We retain personal information only for as long as it is needed to deliver the services, meet legal requirements, or resolve disputes. Retention follows our documented operational schedule, and we delete your data on request; per-workspace retention settings are on the roadmap, not shipped. When data is no longer required, we delete or anonymize it in line with documented schedules.
            </Text>
            <List>
              <ListItem>account data is kept while your account exists; when you ask us to delete it, the timeline below applies.</ListItem>
              <ListItem>agent prompts, transcripts and attachments are kept under our documented retention schedule and deleted on request. per-workspace retention timers are on the roadmap, not shipped.</ListItem>
              <ListItem>support requests and our replies are kept with your account and deleted with it, or sooner if you ask.</ListItem>
              <ListItem>if you start a new organization's free internship, or sign up through a referral link, we keep a one-way hash of the email address, the device and the network it came from for three years, even after an account is deleted, so that each is taken once per person. The record holds no address and nothing else about you.</ListItem>
              <ListItem>server logs are kept for 30 days, and Google Cloud's admin-activity audit logs for 400 days. Most log entries record ids, counts and timings, but some can hold a phone number or part of a message.</ListItem>
              <ListItem>operational metrics that cannot be tied to an individual — error rates, latency, model usage totals — may be kept to inform long-term product strategy.</ListItem>
            </List>
            <Text>
              You may request deletion of your personal data at any time. A deletion request can be cancelled for 30 days; we carry it out after that window closes, and in any case within 45 days of the request, except where a legal hold or legal, tax or regulatory duties require us to keep records.
            </Text>
            <Text>
              Once a deletion is carried out, copies in our daily backups are cleared within 7 days. Our retention schedule, including the records the law requires us to keep, is published on our <PageLink to="/trust">trust page</PageLink>.
            </Text>
          </>
        ),
      },
      {
        id: 'sms-text-messaging',
        heading: 'sms & text messaging',
        body: (
          <>
            {/* PRE-72: /signup links to /privacy-policy#sms. We keep the existing
                section id ('sms-text-messaging') so the in-page TOC and scroll-spy
                keep working, and expose an additional 'sms' anchor inside the
                section body so the shorter URL still scrolls here. */}
            <Anchor id="sms" />
            <Text>
              prest0 uses Twilio to send SMS text messages from (628) 444-4018. By providing your phone number and opting in, you consent to receive text messages from prest0 at the number provided. The following disclosures apply to all SMS communications:
            </Text>
            <List>
              <ListItem>message frequency varies based on your account activity and notification preferences.</ListItem>
              <ListItem>message and data rates may apply. Check with your mobile carrier for applicable rates.</ListItem>
              <ListItem>you can opt out at any time by replying STOP to any message. You will receive one final confirmation message and then no further texts.</ListItem>
              <ListItem>for help, reply HELP to any message or contact us at <OutLink href="mailto:support@prest0.ai">support@prest0.ai</OutLink>.</ListItem>
              <ListItem>we do not share your phone number with third parties for their own marketing purposes.</ListItem>
              <ListItem>consent to receive text messages is not a condition of purchase or use of any prest0 service.</ListItem>
              <ListItem>supported carriers include major US carriers. Carrier message and data rates may apply. Carriers are not liable for delayed or undelivered messages.</ListItem>
            </List>
            <Note>
              <NoteText>
                Text messages from prest0 are transactional and informational in nature—case updates, appointment reminders, and account notifications. We do not send unsolicited marketing texts. Your opt-in consent can be withdrawn at any time by replying STOP.
              </NoteText>
            </Note>

            {/* PRE-72: carrier-compliance-grade SMS disclosures, additive to the
                existing copy above. Twilio A2P reviewers specifically asked us
                to spell out (i) what SMS-specific data we collect, (ii) why,
                (iii) who we share with, (iv) opt-out paths, and (v) retention. */}
            <SubTitle>what sms data we collect</SubTitle>
            <List>
              <ListItem>phone number (normalized to E.164) you provide at opt-in.</ListItem>
              <ListItem>consent timestamp recorded by our servers (UTC).</ListItem>
              <ListItem>IP address captured from your network request at the time of opt-in.</ListItem>
              <ListItem>user-agent string from the browser or device used to opt in.</ListItem>
              <ListItem>double-opt-in confirmation status (pending / confirmed / opted-out / expired).</ListItem>
            </List>
            <SubTitle>why we collect it</SubTitle>
            <List>
              <ListItem>carrier-compliance audit trails for A2P 10DLC, TCPA, and similar regulations.</ListItem>
              <ListItem>routing and delivery of account notifications you explicitly requested.</ListItem>
              <ListItem>fraud prevention and abuse detection (e.g. catching bulk opt-in abuse).</ListItem>
            </List>
            <SubTitle>who receives your number, texts and calls</SubTitle>
            <List>
              <ListItem>Twilio carries our texts and calls, under its standard terms, which include its data protection addendum, and mobile network operators in the delivery path carry them to your phone.</ListItem>
              <ListItem>when you text Prest0n, the AI model providers that write its replies receive the text of your messages, through our model gateway; our trust page names each one.</ListItem>
              <ListItem>when Prest0n speaks on a phone call, Twilio carries the audio and Google's Gemini Live runs the voice, and the call is transcribed.</ListItem>
              <ListItem>on Prest0's own company line, (628) 444-4018, texts and call transcripts, with the number they came from, are also shared with Prest0's team in our internal Slack workspace.</ListItem>
              <ListItem>nobody else. We do not share or sell your phone number to third-party marketers, data brokers, or advertisers.</ListItem>
            </List>
            <SubTitle>how to opt out</SubTitle>
            <List>
              <ListItem>text STOP (or STOPALL / UNSUBSCRIBE / CANCEL / END / QUIT) to any Prest0n message.</ListItem>
              <ListItem>toggle SMS off in your account settings at any time.</ListItem>
              <ListItem>email <OutLink href="mailto:support@prest0.ai">support@prest0.ai</OutLink> to request manual removal.</ListItem>
            </List>
            <SubTitle>retention of sms data</SubTitle>
            <List>
              <ListItem>your phone number is removed from active send lists within 24 hours of opt-out.</ListItem>
              <ListItem>the consent audit record (timestamp, IP, UA, opt-in/opt-out events) is kept for 4 years from the last text we sent under that consent, and an opt-out is kept for as long as we send texts from our number, so that it stays honored. See the dedicated <PageLink to="/sms-terms">SMS Terms</PageLink> page for the full program description.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'data-security',
        heading: 'data security',
        body: (
          <>
            <Text>
              Keeping your data safe is fundamental to our platform design. We implement the same layered safeguards described on the security page, including:
            </Text>
            <List>
              <ListItem>TLS encrypts every session from browser to api, and every browser that supports TLS 1.3 uses it.</ListItem>
              <ListItem>data stored in google cloud firestore and storage is encrypted with aes-256 plus managed key rotation.</ListItem>
              <ListItem>sign-in through google or an email and password, with role-based permissions resolved per person and per message. two-step sign-in with an authenticator app is available to every member, and a workspace can require it for all its members.</ListItem>
              <ListItem>append-only audit logs and automated alerts provide oversight. we commit to notify affected customers within 48 hours of confirming a security incident; the notice goes to each workspace's owners by email and in the app, and a check runs every hour until every affected workspace has received it.</ListItem>
              <ListItem>every request through our model gateway carries zero-data-retention and no-collection flags. calls made straight to a few voice, video and sound vendors are not yet all under those terms, and no training and zero retention at every provider, fallbacks included, is targeted for october 31, 2026.</ListItem>
              <ListItem>google cloud, which runs the infrastructure underneath prest0, holds soc 2 type ii and iso/iec 27001 reports for it; those reports are google's, not prest0's. where our own audits stand is published on the security page, and penetration testing is planned.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'ai-automation',
        heading: 'ai agents & automation',
        body: (
          <>
            <Text>
              prest0 offers ai-powered automation. We apply guardrails to ensure responsible use of data within these workflows:
            </Text>
            <List>
              <ListItem>agents operate inside isolated sandboxes with per-field data protections and debounced persistence to avoid overwriting active edits.</ListItem>
              <ListItem>our server logs mostly record ids, counts and timings, but some entries can hold a phone number or part of a message; they are kept for 30 days.</ListItem>
              <ListItem>when you connect external data sources, we process the information strictly according to your documented instructions.</ListItem>
              <ListItem>automated decisions that may significantly affect users always include human review and override options.</ListItem>
              <ListItem>our no-training commitment means prompts, transcripts and attachments are not to fine-tune shared models; no training and zero retention at every provider is targeted for october 31, 2026.</ListItem>
            </List>
          </>
        ),
      },
      {
        id: 'international-transfers',
        heading: 'where your data is stored',
        body: (
          <>
            <Text>
              Customer production data is stored on Google Cloud in the United States: our databases (Firestore), Cloud Functions and Cloud Run services run in Google Cloud's us-central1 region (Iowa). The exceptions inside the United States are one stopped legacy Windows workstation and some disk snapshots in us-east4 (Virginia), which are moving to us-central1, and some snapshots in Google's US multi-region. Two services are not pinned to a US region: Secret Manager, where we keep platform secrets, replicates them automatically, and our server logs sit in Cloud Logging's global location.
            </Text>
            <Text>
              Two AI providers, Alibaba Cloud and BytePlus, are reached at their Singapore endpoints, and a request through our model gateway goes to the host that serves the model; our trust page names each provider and, where it is known, where it runs.
            </Text>
            <Text>
              Authorized Prest0 staff and contractors reach our systems remotely through their own named accounts, including staff based in Pakistan, who use named Google Workspace accounts, and sales representatives in Venezuela and Nicaragua, who work only in Prest0's own sales tool with our records about prospective customers. Customer workspace content stays stored in the United States as described above.
            </Text>
            <Text>
              If a law that restricts international transfers, such as the GDPR, applies to you, please contact us before you place personal data on the platform, so we can tell you which providers would receive it and what safeguards apply.
            </Text>
          </>
        ),
      },
      {
        id: 'privacy-choices',
        heading: 'your privacy choices',
        body: (
          <>
            <Text>
              You remain in control of your personal information. Depending on your jurisdiction, you may have the right to:
            </Text>
            <List>
              <ListItem>access, review, or export a copy of the data we maintain about you.</ListItem>
              <ListItem>update or correct inaccurate information via account settings or by contacting support.</ListItem>
              <ListItem>request deletion or anonymization of your personal data.</ListItem>
              <ListItem>opt out of non-essential communications, and withdraw any consent you gave, at any time.</ListItem>
              <ListItem>restrict or object to certain processing activities where legally permitted.</ListItem>
            </List>
            <Text>
              We answer verified requests to access, correct, export or delete personal data within 45 days of the request. Where the law allows, we may extend the time to answer an access, correction or export request once, by up to another 45 days, and we tell you within the first 45 days if we do; a deletion is carried out on the timeline in the data retention section above.
            </Text>
            <Text>
              If a firm placed your information on the platform, send your request to that firm; if you send it to us, we will refer you to the firm and help it answer.
            </Text>
            <Note>
              <NoteText>
                You can download a copy of your data and ask us to delete it from your settings page; for any other request, write to us at the address in contact us below. We verify that a request comes from you before we act on it, and we keep a record of what we did.
              </NoteText>
            </Note>
          </>
        ),
      },
      {
        id: 'cookies',
        heading: 'cookies & tracking technologies',
        body: (
          <>
            <Text>
              We use cookies and local storage to keep you signed in and to remember how you have set things up. We set no advertising or analytics cookie and load no third-party tracker, so there is no consent banner here and nothing non-essential for you to decline. Your browser settings refuse or delete what we do set, and we honor Global Privacy Control.
            </Text>
            <Text>
              For a detailed breakdown of cookie categories, retention windows, and opt-out controls, visit our dedicated <PageLink to="/cookie-policy">cookie policy</PageLink>.
            </Text>
          </>
        ),
      },
      {
        id: 'third-parties',
        heading: 'third-party services & integrations',
        body: (
          <>
            <Text>
              To deliver the services, we work with these providers, among others:
            </Text>
            <List>
              <ListItem>google cloud / firebase for secure hosting, authentication, firestore database, and storage.</ListItem>
              <ListItem>stripe for payment processing and financial reporting.</ListItem>
              <ListItem>AI model providers that execute prompts, held to our no-training commitment (no training and zero retention at every provider targeted for October 31, 2026).</ListItem>
              <ListItem>twilio for text messages and phone calls, and resend and sendgrid for email.</ListItem>
              <ListItem>composio, which brokers the applications you connect.</ListItem>
              <ListItem>courtlistener (free law project) for case-law search and citation checks.</ListItem>
              <ListItem>operations tooling for logging, alerting and our team's internal messages (google cloud logging and slack).</ListItem>
            </List>
            <Text>
              Each provider handles what it receives under its own terms or its agreement with us. Our trust page lists them, with what each receives and where it runs.
            </Text>
          </>
        ),
      },
      {
        id: 'children',
        heading: 'children',
        body: (
          <>
            <Text>
              Our services are for businesses and the people who work with them, and our terms of service require account holders to be at least 18. They are not directed to children under 16, and we do not knowingly collect personal information from them. If you believe a child under 16 has given us personal information, contact us and we will delete it.
            </Text>
          </>
        ),
      },
      {
        id: 'policy-updates',
        heading: 'updates to this policy',
        body: (
          <>
            <Text>
              We review this privacy policy regularly to reflect new features, regulatory changes, or operational updates. When we make material changes, we will post a notice on the services, update the effective date above, and provide additional communication if required by law.
            </Text>
          </>
        ),
      },
      {
        id: 'contact',
        heading: 'contact us',
        body: (
          <>
            <Text>
              For privacy questions and requests about your information, write to <OutLink href="mailto:support@prest0.ai">support@prest0.ai</OutLink>, attention: Privacy Officer. For help with text messages, reply HELP to any message or write to the same address.
            </Text>
          </>
        ),
      },
    ],
  };
}
